<template>
  <div>
    <a-card :bordered="false">
      <template slot="title">
        <span style="font-size: 16px; font-weight: 600;">{{ titleText }}</span>
      </template>
      <div class="table-page-search-wrapper">
        <a-form layout="inline" @keyup.enter.native="searchQuery">
          <a-row :gutter="24">
            <a-col :md="5" :sm="24">
              <a-form-item label="起始月份" :labelCol="labelCol" :wrapperCol="wrapperCol">
                <a-month-picker v-model="beginMoment" format="YYYY-MM" style="width:100%" />
              </a-form-item>
            </a-col>
            <a-col :md="5" :sm="24">
              <a-form-item label="截止月份" :labelCol="labelCol" :wrapperCol="wrapperCol">
                <a-month-picker v-model="endMoment" format="YYYY-MM" style="width:100%" />
              </a-form-item>
            </a-col>
            <a-col :md="5" :sm="24">
              <a-form-item label="门店" :labelCol="labelCol" :wrapperCol="wrapperCol">
                <store-tenant-select domain="finance" @change="handleStoreTenantChange"></store-tenant-select>
              </a-form-item>
            </a-col>
            <a-col :md="9" :sm="24" style="text-align: right;">
              <a-button type="primary" :loading="loading" @click="searchQuery">刷新</a-button>
              <a-button style="margin-left: 8px" @click="printPage">打印</a-button>
              <a-checkbox :checked="showQty" style="margin-left: 16px" @change="onQtyToggle">显示数量金额</a-checkbox>
            </a-col>
          </a-row>
        </a-form>
      </div>
      <div style="display: flex;">
        <div style="flex: 1 1 0%; overflow-x: auto;">
          <a-table bordered size="middle" rowKey="key" :columns="columns" :dataSource="dataSource"
            :pagination="false" :loading="loading" :rowClassName="rowClassName" :scroll="tableScroll">
            <template slot="voucherNo" slot-scope="text, record">
              <a v-if="record._type === 'normal' && record.voucherId" @click="showVoucher(record)">{{ text }}</a>
              <span v-else>{{ text }}</span>
            </template>
          </a-table>
        </div>
        <div class="subject-panel">
          <a-input v-model="subjectKeyword" placeholder="请输入关键词查询" allowClear />
          <div class="subject-list">
            <div v-for="s in filteredSubjects" :key="s.id" class="subject-item"
              :class="{ active: s.id === subjectId }" :title="s.subjectName"
              @click="onSubjectClick(s)">{{ s.subjectCode }}_{{ s.subjectName }}</div>
          </div>
        </div>
      </div>
    </a-card>
    <!-- 凭证查看弹窗（点击凭证字号） -->
    <voucher-editor :visible="editorVisible" mode="view" :account-set-id="accountSetId"
      :voucher-id="editorVoucherId" @update:visible="v => editorVisible = v" />
  </div>
</template>

<script>
import moment from 'moment'
import VoucherEditor from './VoucherEditor'
import { ledgerAccountQuery, subjectLeafList, accountSetCurrent } from './api'
import StoreTenantSelect from '@/components/StoreTenantSelect'

const fmt2 = v => (v === null || v === undefined || v === '' || isNaN(Number(v))) ? '' : Number(v).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtQ = v => (v === null || v === undefined || v === '') ? '' : String(Number(v))

export default {
  name: 'DetailedAccountList',
  components: { VoucherEditor, StoreTenantSelect },
  data() {
    return {
      labelCol: { span: 8 }, wrapperCol: { span: 15, offset: 1 },
      accountSetId: null,
      subjects: [],
      subjectKeyword: '',
      subjectId: null,
      subjectInfo: {},
      beginMoment: null,
      endMoment: null,
      showQty: false,
      loading: false,
      dataSource: [],
      tableScroll: { x: 'max-content', y: 400 },
      editorVisible: false,
      editorVoucherId: null,
      baseColumns: [
        { title: '日期', dataIndex: 'voucherDate', width: 105 },
        { title: '凭证字号', dataIndex: 'voucherNo', width: 95, scopedSlots: { customRender: 'voucherNo' } },
        { title: '摘要', dataIndex: 'summary', width: 170 },
        { title: '核算维度', dataIndex: 'auxText', width: 150 },
        { title: '对方科目', dataIndex: 'oppositeText', width: 150 },
        { title: '借方', dataIndex: 'debitAmount', align: 'right', width: 110 },
        { title: '贷方', dataIndex: 'creditAmount', align: 'right', width: 110 },
        { title: '方向', dataIndex: 'direction', align: 'center', width: 60 },
        { title: '余额', dataIndex: 'balance', align: 'right', width: 110 }
      ]
    }
  },
  computed: {
    titleText() {
      return this.subjectInfo && this.subjectInfo.name ? this.subjectInfo.name + ' - 明细账' : '明细账'
    },
    filteredSubjects() {
      if (!this.subjectKeyword) return this.subjects
      const k = this.subjectKeyword.toLowerCase()
      return this.subjects.filter(s =>
        (s.subjectCode || '').toLowerCase().indexOf(k) >= 0 ||
        (s.subjectName || '').toLowerCase().indexOf(k) >= 0)
    },
    columns() {
      if (!this.showQty) return this.baseColumns
      const cols = this.baseColumns.slice()
      cols.splice(7, 0, { title: '数量', dataIndex: 'qty', align: 'right', width: 90 })
      cols.splice(8, 0, { title: '单价', dataIndex: 'unitPrice', align: 'right', width: 90 })
      return cols
    }
  },
  created() {
    this.beginMoment = moment()
    this.endMoment = moment()
    this.loadCurrent()
  },
  mounted() {
    this.$nextTick(() => {
      const el = this.$el.querySelector('.ant-table')
      if (el) {
        const top = el.getBoundingClientRect().top
        this.tableScroll.y = Math.max(300, window.innerHeight - top - 24)
      }
    })
  },
  methods: {
    loadCurrent() {
      accountSetCurrent({}).then((res) => {
        if (res && res.success && res.data && res.data.id) {
          this.accountSetId = res.data.id
          this.loadSubjects()
        } else { this.$message.warning('尚未建账或未设当前账套') }
      })
    },
    loadSubjects() {
      subjectLeafList({ accountSetId: this.accountSetId, hasValue: true }).then((res) => {
        if (res && res.success) {
          this.subjects = res.data || []
          if (this.subjects.length) this.onSubjectClick(this.subjects[0])
          else this.$message.warning('无有数值的科目')
        }
      })
    },
    onSubjectClick(s) {
      if (this.subjectId === s.id) { this.searchQuery(); return }
      this.subjectId = s.id
      this.subjectInfo = { code: s.subjectCode, name: s.subjectName }
      this.searchQuery()
    },
    onQtyToggle(e) { this.showQty = e.target.checked },
    searchQuery() {
      if (!this.subjectId) { this.$message.warning('请选择科目'); return }
      this.loading = true
      const p = { subjectId: this.subjectId }
      if (this.beginMoment) p.beginPeriod = this.beginMoment.format('YYYY-MM')
      if (this.endMoment) p.endPeriod = this.endMoment.format('YYYY-MM')
      ledgerAccountQuery(p).then((res) => {
        if (res && res.success && res.data) {
          const d = res.data
          if (d.subject) {
            this.subjectInfo = d.subject
            if (d.subject.bookFormat === '数量金额式') this.showQty = true
          }
          this.dataSource = this.buildRows(d)
        } else {
          if (res && !res.success && res.message) this.$message.warning(res.message)
          this.dataSource = []
        }
        this.loading = false
      }).catch(() => { this.loading = false })
    },
    buildRows(d) {
      const rows = []
      const openDate = this.beginMoment ? this.beginMoment.format('YYYY-MM') + '-01' : ''
      const o = d.opening || {}
      rows.push({ key: 'opening', _type: 'opening', voucherDate: openDate, summary: '期初余额',
        direction: o.direction || '平', balance: fmt2(o.balance) })
      let hasQty = false
      let qtyNet = 0
      ;(d.rows || []).forEach((r, i) => {
        const debit = Number(r.debitAmount) || 0
        const credit = Number(r.creditAmount) || 0
        if (r.qty !== null && r.qty !== undefined) {
          hasQty = true
          qtyNet += debit !== 0 ? Number(r.qty) : -Number(r.qty)
        }
        rows.push({
          key: r.id || ('r' + i),
          _type: 'normal',
          voucherId: r.voucherId,
          voucherDate: r.voucherDate ? String(r.voucherDate).substring(0, 10) : '',
          voucherNo: (r.word && r.code != null) ? r.word + '-' + String(r.code).padStart(3, '0') : (r.voucherNo || ''),
          summary: r.summary || '',
          auxText: r.auxText || '',
          oppositeText: r.oppositeText || '',
          debitAmount: fmt2(r.debitAmount),
          creditAmount: fmt2(r.creditAmount),
          qty: fmtQ(r.qty),
          unitPrice: fmtQ(r.unitPrice),
          direction: r.direction || '',
          balance: fmt2(r.balance)
        })
      })
      const endDate = this.endMoment ? this.endMoment.clone().endOf('month').format('YYYY-MM-DD') : ''
      const pt = d.periodTotal || {}
      rows.push({ key: 'periodTotal', _type: 'total', voucherDate: endDate, summary: '本期合计',
        debitAmount: fmt2(pt.debit), creditAmount: fmt2(pt.credit), qty: hasQty ? fmtQ(qtyNet) : '',
        direction: pt.direction || '', balance: fmt2(pt.balance) })
      const yt = d.yearTotal || {}
      rows.push({ key: 'yearTotal', _type: 'total', voucherDate: endDate, summary: '本年累计',
        debitAmount: fmt2(yt.debit), creditAmount: fmt2(yt.credit), qty: hasQty ? fmtQ(qtyNet) : '',
        direction: yt.direction || '', balance: fmt2(yt.balance) })
      return rows
    },
    rowClassName(record) { return record._type === 'normal' ? '' : 'gl-detail-agg-row' },
    showVoucher(record) {
      this.editorVoucherId = record.voucherId
      this.editorVisible = true
    },
    printPage() { window.print() },
    handleStoreTenantChange() { this.searchQuery() }
  }
}
</script>

<style scoped>
.subject-panel { width: 230px; flex: none; margin-left: 10px; border: 1px solid #e8e8e8; border-radius: 4px; padding: 8px; }
.subject-list { max-height: 520px; overflow-y: auto; margin-top: 8px; }
.subject-item { padding: 4px 8px; cursor: pointer; border-radius: 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 13px; }
.subject-item:hover { background: #f0f6ff; }
.subject-item.active { background: #e6f7ff; color: #1890ff; }
/deep/ .gl-detail-agg-row td { background: #fafafa !important; font-weight: 600; }
</style>
